import React, { useEffect, useRef, useState } from 'react';
import { Button, Row } from 'react-bootstrap';
import CreateCompanyForm from './CreateCompanyForm';
import CustomModal, {
  CustomModalProps,
  ModalHandle
} from 'components/modals/CustomModal';
import { PurchaseOrderHandle } from '../PurchaseOrder/CreatePurchaseOrderForm';
import axiosInstance from 'utils/axiosInterceptors';
import { ColumnDef } from '@tanstack/react-table';
import { TCompanyCustomer } from 'utils/types';
import DataTable from '../BranchProductInventory/DataTable';
import { Link } from 'react-router-dom';

export type CompanyCustomer = {
  email: string;
  contactNumber: string;
  companyName: string;
  typeOfBusiness: string;
  taxIdentificationNumber: string;
  isRelatedToAutomotiveBusiness: boolean;
  paymentTerms: number;
  address: {
    addressLine1: string;
    addressLine2: string;
    barangay: string;
    city: string;
    province: string;
    zipCode: string;
  };
  user: {
    email: string;
    firstName: string;
    lastName: string;
    mobileNumber: string;
  };
  membershipCardNumber: string;
};

const CompanyCustomerList = () => {
  const [modalProps, setModalProps] = useState<CustomModalProps>();
  const modalRef = useRef<ModalHandle>({
    show: () => {},
    hide: () => {}
  });
  const addCompanyCustomerFormRef = useRef<PurchaseOrderHandle>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const fetchData = async (
    page: number,
    pageSize: number,
    search: string,
    orderBy: string,
    orderByDesc: boolean
  ) => {
    const { data } = await axiosInstance.get(`/api/v1/customers/companies/`, {
      params: {
        page,
        pageSize,
        search,
        orderBy,
        orderByDesc
      }
    });
    return data;
  };

  const createCompanyCustomer = () => {
    setModalProps({
      title: 'Create Customer',
      content: <CreateCompanyForm ref={addCompanyCustomerFormRef} />,
      onCancel: () => modalRef.current.hide(),
      onSubmit: () => handleSubmitCompanyCustomer()
    });
    modalRef.current.show();
  };

  const handleSubmitCompanyCustomer = async () => {
    if (addCompanyCustomerFormRef.current) {
      try {
        const isSuccess = await addCompanyCustomerFormRef.current.submit();
        if (isSuccess) {
          modalRef.current.hide();
          setIsSubmitted(true);
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  const columns: ColumnDef<TCompanyCustomer>[] = [
    {
      id: 'companyName',
      header: 'Name',
      accessorKey: 'companyName',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        return (
          <Link
            to={`/company-details/${original.customerId}`}
            className="fw-bold"
          >
            {original.companyName}
          </Link>
        );
      }
    },
    {
      id: 'typeOfBusiness',
      header: 'Type',
      accessorKey: 'typeOfBusiness',
      enableSorting: false
    },
    {
      id: 'city',
      header: 'City',
      accessorKey: 'city',
      enableSorting: false
    },
    {
      id: 'membershipNumber',
      header: 'Membership #',
      accessorKey: 'membershipNumber',
      enableSorting: false
    },
    {
      id: 'email',
      header: 'Email',
      accessorKey: 'email',
      enableSorting: false
    },
    {
      id: 'contactNumber',
      header: 'Contact Number',
      accessorKey: 'contactNumber',
      enableSorting: false
    }
  ];

  useEffect(() => {
    if (isSubmitted) {
      setIsSubmitted(false);
    }
  }, [isSubmitted]);

  return (
    <>
      <Row lg="1" className="mb-3">
        <h4>Company List</h4>
      </Row>
      <DataTable
        queryKey={['companyCustomer', isSubmitted]}
        queryFn={fetchData}
        columns={columns}
        rightSideActions={
          <>
            <Row lg="auto">
              <Button
                variant="primary"
                className="w-10 mb-3"
                onClick={createCompanyCustomer}
              >
                Create Customer
              </Button>
            </Row>
          </>
        }
      />
      <CustomModal
        ref={modalRef}
        title={modalProps?.title}
        content={modalProps?.content}
        onCancel={modalProps?.onCancel}
        onSubmit={modalProps?.onSubmit}
        modalSize="lg"
      />
    </>
  );
};

export default CompanyCustomerList;
